import { TriangleAlert } from "lucide-react";
import { useLocation, useNavigate } from "react-router";

import { useAuthUser } from "@/context/auth/AuthContext";
import { useBusinessBilling } from "@/hooks/react-query/Billing/useBusinessBilling";
import {
  avisoDeMora,
  textoDelAviso,
} from "@/hooks/react-query/Billing/avisoDeMora";
import { fecha } from "@/pages/subscription/columnasDeExport";
import { recuperacion } from "@/hooks/react-query/Billing/recuperacion";
import {
  AVISO_DE_MODO_LECTURA,
  enModoLectura,
} from "../billing-access/modoLectura";

const MI_SUSCRIPCION = "/mi-suscripcion";

/**
 * Banda del dueño: mora previa al corte y próximos pasos de recuperación.
 *
 * # Por qué en el layout
 *
 * Un cartel el día del bloqueo llega tarde. La cuenta regresiva concreta es el
 * punto: "estás en mora" no mueve a nadie, "en 5 días se suspende tu cuenta"
 * sí. Por eso vive en el layout —se ve en todas las pantallas— y no sólo en
 * Mi Suscripción.
 *
 * # El dueño y el empleado
 *
 * El resumen es el carril del dueño: un empleado no lo puede leer, y además no
 * es quien paga. Al empleado este componente no le consulta ni expone el
 * billing: si el negocio está suspendido —el claim `suspended` de su token—, le
 * dice que la app está en modo lectura y que hable con el administrador
 * (trd-client#269).
 *
 * # No bloquea nada
 *
 * Es un aviso: no tapa la pantalla ni impide navegar. El corte lo aplica el
 * borde; BillingAccessBoundary refleja el claim de suspensión del dueño.
 */
function AvisoDeMora() {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const usuario = useAuthUser();
  const esDueno = usuario?.type === "owner";

  const billing = useBusinessBilling({ habilitado: esDueno });
  if (!esDueno)
    return enModoLectura(usuario) ? (
      <Banda texto={AVISO_DE_MODO_LECTURA} />
    ) : null;
  if (billing.estado === "cargando") return null;

  let texto: string | null = null;
  if (billing.estado === "error")
    texto =
      "No pudimos consultar tu suscripción. No podemos confirmar el estado del servicio.";
  if (billing.estado === "sin-suscripcion")
    texto =
      "Este negocio no tiene una suscripción. Revisá cómo contratar el servicio.";
  if (billing.estado === "listo") {
    const estado = recuperacion(billing.resumen);
    if (estado.tipo !== "al-dia" && estado.tipo !== "pagar")
      texto = estado.titulo;
    else if (billing.resumen.suspended)
      texto =
        "Tu negocio está suspendido. Revisá tu suscripción para recuperar el servicio.";
    else {
      const aviso = avisoDeMora(billing.resumen);
      if (aviso)
        texto = textoDelAviso(aviso, aviso.fecha ? fecha(aviso.fecha) : null);
      else if (estado.tipo === "pagar") texto = "Tenés un pago pendiente.";
    }
  }
  if (!texto) return null;

  return (
    <Banda texto={texto}>
      {billing.estado === "error" && (
        <button
          type="button"
          className="min-h-10 px-2 underline font-medium"
          onClick={() => void billing.refetch()}
        >
          Reintentar
        </button>
      )}
      {pathname !== MI_SUSCRIPCION && (
        <button
          type="button"
          className="min-h-10 px-2 underline cursor-pointer font-medium"
          onClick={() => navigate(MI_SUSCRIPCION)}
        >
          Ver mi suscripción
        </button>
      )}
    </Banda>
  );
}

function Banda({
  texto,
  children,
}: {
  texto: string;
  children?: React.ReactNode;
}) {
  return (
    <div
      role="status"
      className="flex flex-wrap justify-center items-center gap-x-3 gap-y-1 px-3 py-2 border-b bg-alert-warning-background border-alert-warning-border text-alert-warning-foreground"
    >
      <TriangleAlert className="h-4 w-4 shrink-0 text-alert-warning-icon" />
      <p className="font-medium text-pretty">{texto}</p>
      {children}
    </div>
  );
}

export default AvisoDeMora;
